import { findSceneNode, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { BackgroundBlurEffect } from "@/document/model/styles";
import { Renderer } from "@/renderer/Renderer";
import { prepareWasmMeshKernels } from "@/renderer/scene/tessellation/curvaTessWasm";

export async function copySceneNodeAsPng(document: CurvaDocument, node: SceneNode, scale = 1): Promise<void> {
  if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
    throw new Error("PNG clipboard copy is not supported in this browser");
  }

  const blob = await exportSceneNodeToPngBlob(document, node, scale);
  await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
}

/**
 * A reusable renderer + offscreen canvas for exporting many nodes without
 * paying the (expensive) WebGL context + shader-compilation cost per node.
 *
 * Creating a fresh {@link Renderer} for every export allocates a new WebGL2
 * context each time. Browsers hard-limit the number of live contexts and start
 * force-losing the oldest ones once that limit is exceeded, so exporting a large
 * batch (e.g. hundreds of frames to a single PDF) one-renderer-per-node freezes
 * the tab. Hold a single session for the whole batch instead.
 */
export interface PngExportSession {
  renderNodeToPngBlob(document: CurvaDocument, node: SceneNode, scale?: number): Promise<Blob>;
  dispose(): void;
}

export function createPngExportSession(): PngExportSession {
  const canvas = window.document.createElement("canvas");
  canvas.style.cssText = "position:fixed;left:-10000px;top:0;pointer-events:none;";
  window.document.body.appendChild(canvas);
  const renderer = new Renderer(canvas, { preserveDrawingBuffer: true });
  const meshReady = prepareWasmMeshKernels();
  let disposed = false;
  return {
    async renderNodeToPngBlob(document: CurvaDocument, node: SceneNode, scale = 1): Promise<Blob> {
      if (disposed) {
        throw new Error("PNG export session has been disposed");
      }
      await meshReady;
      const exportDocument = documentForPngExport(document, node);
      return renderer.renderDocumentToPngBlob(exportDocument, node.bounds, scale);
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      renderer.dispose();
      canvas.remove();
    },
  };
}

export async function exportSceneNodeToPngBlob(document: CurvaDocument, node: SceneNode, scale = 1): Promise<Blob> {
  const session = createPngExportSession();
  try {
    return await session.renderNodeToPngBlob(document, node, scale);
  } finally {
    session.dispose();
  }
}

export async function exportSceneNodeThumbnailDataUrl(
  document: CurvaDocument,
  node: SceneNode,
  boxSize = 80,
): Promise<string> {
  await prepareWasmMeshKernels();
  const canvas = window.document.createElement("canvas");
  const renderer = new Renderer(canvas, { alpha: false, preserveDrawingBuffer: true });
  try {
    const exportDocument = documentForPngExport(document, node);
    const blob = await renderer.renderDocumentThumbnail(exportDocument, node.bounds, boxSize);
    return await blobToDataUrl(blob);
  } finally {
    renderer.dispose();
  }
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read thumbnail blob"));
    reader.readAsDataURL(blob);
  });
}

interface SceneNodeParentRef {
  type: "page" | "frame" | "group" | "component" | "componentVariant" | "instance";
  id: string;
}

interface SceneNodeLocation {
  node: SceneNode;
  index: number;
  parent: SceneNodeParentRef;
  children: SceneNode[];
}

function isContainerNode(node: SceneNode): boolean {
  return node.type === "frame"
    || node.type === "group"
    || node.type === "component"
    || node.type === "componentVariant"
    || node.type === "instance";
}

function hasVisibleBackgroundBlur(node: SceneNode): boolean {
  return (node.style.effects ?? []).some(
    (effect): effect is BackgroundBlurEffect =>
      effect.visible && effect.type === "backgroundBlur" && effect.blur > 0,
  );
}

function containerParentType(node: SceneNode): SceneNodeParentRef["type"] {
  if (node.type === "frame") {
    return "frame";
  }
  if (node.type === "group") {
    return "group";
  }
  if (node.type === "component") {
    return "component";
  }
  if (node.type === "componentVariant") {
    return "componentVariant";
  }
  return "instance";
}

function setNodeChildren(node: SceneNode, children: SceneNode[]): void {
  if (node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    node.children = children;
  }
}

function findSceneNodeLocation(document: CurvaDocument, nodeId: string): SceneNodeLocation | undefined {
  for (const page of document.pages) {
    const location = findSceneNodeLocationInChildren(page.children, nodeId, { type: "page", id: page.id });
    if (location) {
      return location;
    }
  }
  return undefined;
}

function findSceneNodeLocationInChildren(
  children: SceneNode[],
  nodeId: string,
  parent: SceneNodeParentRef,
): SceneNodeLocation | undefined {
  const index = children.findIndex(node => node.id === nodeId);
  if (index >= 0) {
    return { node: children[index]!, index, parent, children };
  }

  for (const child of children) {
    const nestedChildren = getSceneNodeChildren(child);
    if (!nestedChildren) {
      continue;
    }

    const location = findSceneNodeLocationInChildren(nestedChildren, nodeId, {
      type: containerParentType(child),
      id: child.id,
    });
    if (location) {
      return location;
    }
  }

  return undefined;
}

function resolveParentNode(document: CurvaDocument, parent: SceneNodeParentRef): SceneNode | undefined {
  if (parent.type === "page") {
    return undefined;
  }
  return findSceneNode(document, parent.id);
}

function buildBackdropExportTree(document: CurvaDocument, nodeId: string, exportNode: SceneNode): SceneNode {
  const location = findSceneNodeLocation(document, nodeId);
  if (!location) {
    return exportNode;
  }

  let current: SceneNode = exportNode;
  let currentLocation = location;

  for (;;) {
    const parentNode = resolveParentNode(document, currentLocation.parent);
    if (!parentNode) {
      break;
    }

    const parentCopy = structuredClone(parentNode);
    const childrenBefore = currentLocation.children
      .slice(0, currentLocation.index)
      .map(child => structuredClone(child));
    setNodeChildren(parentCopy, [...childrenBefore, current]);
    current = parentCopy;

    if (currentLocation.parent.type === "page") {
      break;
    }

    const parentLocation = findSceneNodeLocation(document, currentLocation.parent.id);
    if (!parentLocation) {
      break;
    }
    currentLocation = parentLocation;
  }

  return current;
}

export function documentForPngExport(document: CurvaDocument, node: SceneNode): CurvaDocument {
  const exportPageId = "png-export-page";
  const cloned = structuredClone(node);
  const exportRoot = isContainerNode(node)
    ? cloned
    : hasVisibleBackgroundBlur(node)
      ? buildBackdropExportTree(document, node.id, cloned)
      : cloned;

  return {
    ...document,
    pages: [{
      id: exportPageId,
      name: "PNG Export",
      children: [exportRoot],
    }],
  };
}
